import { Radio, RadioGroup, Stack } from "@chakra-ui/react";
import axios from "axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import SearchDropdown from "../../../components/Dropdowns/SearchDropdown";
import CheckboxSearchOptions from "../../../components/FilterComponents/CheckboxSearchOptions";
import FilterFieldHeader from "../../../components/FilterComponents/FilterFieldHeader";
import FilterTabData from "../../../components/FilterComponents/FilterTabData";
import OptionalOption from "../../../components/FilterComponents/OptionalOption";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";
import { filterThreeTabData } from "../../../utils/options";

const LeadsFilterData = ({ isSidebarOpen, filterData, onSetFilterData, from }) => {
  const [config] = useAuth();
  const [loading, setLoading] = useState(false);
  const [bdsOptions, setBdsOptions] = useState([]);
  const [linkOptions, setLinkOptions] = useState([]);
  const [contactData, setContactData] = useState([]);
  const [buyerOptions, setBuyerOptions] = useState([]);
  const [brokerOptions, setBrokerOptions] = useState([]);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [leadSourcesOptions, setLeadSourcesOptions] = useState([]);

  const handleFilterChange = (value, name) => {
    onSetFilterData({ ...filterData, [name]: value });
  };

  const fetchSuggestions = (inputValue) => {
    axios
      .get(`${BASE_URL}/get-datalisting?list=contacts&search=${inputValue}`, config)
      .then((res) => {
        const data = res.data.data.contact_list;
        const options = data.map((contact) => ({ value: contact.id, label: `${contact.first_name} ${contact.last_name}` }));
        setContactData(options);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  const fetchPropertySuggestions = (inputValue) => {
    axios
      .get(`${BASE_URL}/get-datalisting?list=properties&search=${inputValue}`, config)
      .then((res) => {
        const data = res.data.data.properties;
        const options = data.map((el) => ({ value: el.id, label: el?.property_name, desc: `${el.street_address}, ${el.city}, ${el.state}` }));
        setPropertyOptions(options);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  const fetchBuyers = () => {
    axios
      .get(`${BASE_URL}/get-datalisting?list=buyer`, config)
      .then((res) => {
        const value = res?.data?.data;
        const acquisitionOptions = value?.buyer?.map((el) => ({ value: el?.id, label: el?.property_type?.[0]?.type, minPrice: el?.min_price, maxPrice: el?.max_price }));
        setBuyerOptions(acquisitionOptions);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  const fetchLeadSources = () => {
    axios
      .get(`${BASE_URL}/get-datalisting?list=lead_sources`, config)
      .then((res) => {
        const value = res?.data?.data?.lead_sources;
        setLeadSourcesOptions(value?.map((el) => ({ value: el?.id, label: el?.name, category: el?.category })));
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  const fetchUsers = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/get-datalisting?list=users`, config)
      .then((res) => {
        const data = res?.data?.data;
        const options = data?.map((el) => ({ value: el?.id, label: `${el?.first_name} ${el?.last_name}`, role: el?.role_id }));
        setBdsOptions(options);
        setBrokerOptions(options?.filter((el) => el.role === 3));
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchLinks = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/get-ContactData/${filterData?.contact}`, config)
      .then((res) => {
        const data = res?.data?.data;
        const properties = data?.properties;
        const acquisitions = data?.acquisitions;
        const propertyOptions = properties?.map((el) => ({ value: el?.id, label: el?.property_name, desc: `${el.street_address}, ${el.city}, ${el.state}` }));
        const acquisitionOptions = acquisitions?.map((el) => ({ value: el?.id, label: el?.property_type?.[0]?.type, minPrice: el?.min_price, maxPrice: el?.max_price }));
        if (filterData.lead_type === "acquisition") {
          setLinkOptions(acquisitionOptions);
        } else {
          setLinkOptions(propertyOptions);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const lastUpdateHandler = () => {
    onSetFilterData({ ...filterData, last_contact: "date", lastContactSentDate: "" });
  };

  useEffect(() => {
    if (isSidebarOpen) {
      fetchLeadSources();
      fetchUsers();
      fetchBuyers();
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    if (isSidebarOpen) {
      if (filterData?.contact !== "") {
        fetchLinks();
      }
    }
  }, [isSidebarOpen, filterData?.contact, filterData?.lead_type]);

  const formatOptionLabel = ({ value, label, minPrice, maxPrice, desc }) => (
    <div>
      <div>{label}</div>
      {desc && <p className="">{desc}</p>}
      {(minPrice || maxPrice) && (
        <div className="flex gap-2">
          {minPrice && <p>Min Price-{minPrice}</p>}/{maxPrice && <p>Max Price-{maxPrice}</p>}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div>
        <div className="flex justify-between items-center">
          <p className="head-4 dark-H">BDS</p>
        </div>

        <div className="w-[100%] mt-2">
          <Select
            className="body-S"
            placeholder="Search here..."
            value={bdsOptions?.find((el) => el?.value == filterData?.bds) || null}
            options={bdsOptions}
            onChange={(option) => {
              handleFilterChange(option?.value, "bds");
            }}
          />
        </div>
      </div>

      <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

      <div>
        <p className="head-4 dark-H">Broker</p>

        <div className="w-[100%] mt-2">
          <Select
            className="body-S"
            placeholder="Search here ..."
            value={brokerOptions?.find((el) => el?.value == filterData?.broker) || null}
            options={brokerOptions}
            onChange={(option) => {
              handleFilterChange(option.value, "broker");
            }}
          />
        </div>
      </div>

      <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

      <div>
        <p className="head-4 dark-H">Contact</p>

        <div className="mt-2">
          <SearchDropdown
            isTop={false}
            placeholder={`Search contact`}
            text={filterData?.contact?.name}
            options={contactData}
            fetchSuggestions={fetchSuggestions}
            onSetOptions={(value) => setContactData(value)}
            handleSelect={(option) => {
              onSetFilterData({
                ...filterData,
                contact: { id: option?.value, name: option?.label },
              });
              setContactData([]);
            }}
          />
        </div>
      </div>

      <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />
       
      <div>
          <FilterFieldHeader
            title={"Lead Source"}
            data={filterData?.lead_source?.length}
            type="array"
            handleCross={() => {
              handleFilterChange([], "lead_source");
            }}
          />

          <CheckboxSearchOptions
            handleChange={(value) => {
              handleFilterChange(value, "lead_source");
            }}
            selectedOptions={filterData?.lead_source}
            options={leadSourcesOptions}
          />
        </div>

      <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />


      <div className="mt-4">
        <p className="head-4 dark-H mb-2">Lead Type</p>

        <RadioGroup
          onChange={(value) => {
            handleFilterChange(value, "lead_type");
          }}
          value={filterData?.lead_type}
        >
          <Stack direction="row" gap={5}>
            <Radio value="disposition">Disposition</Radio>
            <Radio value="acquisition">Acquisition</Radio>
          </Stack>
        </RadioGroup>
      </div>

    

      {from === "lead" && (
        
        <>
            <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />
          <div>
            <FilterTabData
              title={"Last Contacted"}
              tabData={filterThreeTabData}
              activeTab={filterData?.last_contact}
              lastDate={filterData?.lastContactSentDate}
              handleLastUpdate={lastUpdateHandler}
              onSetActiveTab={(value) => {
                onSetFilterData({ 
                  ...filterData, 
                  last_contact: value, 
                  lastContactSentDate: "", 
                  lastContactSentSdate: "", 
                  lastContactSentEdate: "", 
                  date_category: "days", 
                  within_last_day: "", 
                  longer_than_day: "" 
                });
              }}
            />
        
            <OptionalOption
              activeTab={filterData?.last_contact}
              onSetLastDate={(value) => handleFilterChange(value, "lastContactSentDate")}
              date={filterData?.lastContactSentDate}
              onSetDateRange1={(value) => handleFilterChange(value, "lastContactSentSdate")}
              date2={filterData?.lastContactSentSdate}
              onSetDateRange2={(value) => handleFilterChange(value, "lastContactSentEdate")}
              date3={filterData?.lastContactSentEdate}
              category={filterData?.date_category}
              onSetCategory={(value) => handleFilterChange(value, "date_category")}
              lastUp_days={
                filterData?.last_contact === "within_last"
                  ? filterData?.within_last_day
                  : filterData?.last_contact === "longer_than"
                  ? filterData?.longer_than_day
                  : ""
              }
              onSetDays={(value) => {
                onSetFilterData({
                  ...filterData,
                  within_last_day: filterData?.last_contact === "within_last" ? value : "",
                  longer_than_day: filterData?.last_contact === "longer_than" ? value : "",
                });
              }}
              onClear={() => {
                onSetFilterData({
                  ...filterData,
                  lastContactSentDate: "",
                  lastContactSentSdate: "",
                  lastContactSentEdate: "",
                });
              }}
            />
          </div>
        </>
      )}

      <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

      <div className="mt-4">
        <label className="dark-H head-4 mb-4">Acquisition Criteria</label>
        <div className="mt-2">
          <Select
            className="body-N"
            options={buyerOptions}
            formatOptionLabel={formatOptionLabel}
            placeholder={"Select"}
            value={buyerOptions.find((option) => option.value === filterData.buyer_id) || null}
            onChange={(option) => {
              handleFilterChange(option.value, "buyer_id");
            }}
          />
        </div>

        <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />
      </div>

      <div className="mt-4">
        <label className="dark-H head-4 mb-4">Property</label>
        <div className="mt-2">
          <SearchDropdown
            isTop={false}
            placeholder={`Search Property`}
            text={filterData?.property?.name}
            options={propertyOptions}
            fetchSuggestions={fetchPropertySuggestions}
            onSetOptions={(value) => setPropertyOptions(value)}
            handleSelect={(option) => {
              onSetFilterData({
                ...filterData,
                property: { id: option?.value, name: option?.label },
              });
              setPropertyOptions([]);
            }}
          />
        </div>

        <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />
      </div>

      <div>
        <p className="head-4 dark-H capitalize">{from} Date</p>

        <OptionalOption
          activeTab="date"
          onSetLastDate={(value) => handleFilterChange(value, "created_at")}
          date={filterData?.created_at}
          onSetDateRange1={(value) => {
            handleFilterChange(value, "created_start_date");
          }}
          date2={filterData?.created_start_date}
          onSetDateRange2={(value) => handleFilterChange(value, "created_end_date")}
          date3={filterData?.created_end_date}
          onClear={() => {
            onSetFilterData({ ...filterData, created_at: "", created_start_date: "", created_end_date: "" });
          }}
        />
      </div>
    </div>
  );
};

export default LeadsFilterData;